import { Component, uiElement, uiElements, uiEvent } from "@kluntje/core";
import { addClass, find, removeClass } from "@kluntje/js-utils/lib/dom-helpers";
import { DebuggerService } from "@kluntje/services";
import { FormField } from "Components/abstract/sde-e-form-field/sde-e-form-field";
import { FormFieldSubmitValue } from "Components/abstract/sde-e-form-field/sde-e-form-field.types";
import { ERROR, LOADING, SUCCESS } from "Constants/cssClasses";
import WindowScrollService from "Services/WindowScrollService";
import { GENERIC_VALIDATION_ERROR_EVENT } from "Constants/eventTypes";

export type FormFieldType = string | boolean | Array<string>;

export class SDEForm extends Component {
  private _isLoading = false;

  @uiElement(".sde-m-form__form")
  protected form: HTMLFormElement;

  @uiElements(".sde-m-form__input")
  private formFields: Array<FormField<FormFieldType>>;

  private get isLoading(): boolean {
    return this._isLoading;
  }

  private set isLoading(newValue: boolean) {
    if (newValue === this._isLoading) return;

    if (newValue === true) {
      addClass(this, LOADING);
    } else {
      removeClass(this, LOADING);
    }

    this._isLoading = newValue;
  }

  protected get submitUrl(): string {
    return this.getAttribute("submit-url") || "";
  }

  protected get submitData(): Array<FormFieldSubmitValue<FormFieldType>> {
    const curSubmitData = this.formFields
      .map(formField => formField.submitData)
      .filter(submitData => submitData !== null);
    // typescript does not figure out !== null filter
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return curSubmitData;
  }

  protected get submitBody(): Record<string, any> | undefined {
    const submitData = this.submitData;
    if (submitData.length === 0) {
      return undefined;
    }

    return {
      formData: this.submitData,
    };
  }

  @uiEvent("form", "submit")
  protected async handleSubmit(e: Event) {
    e.preventDefault();
    await this.submitForm();
  }

  public async submitForm(preventScrolling = false) {
    const allFieldsValid = await SDEForm.validateFormFields(this.formFields);

    if (allFieldsValid === false) {
      this.dispatchEvent(new CustomEvent(GENERIC_VALIDATION_ERROR_EVENT, { bubbles: false }));
      const errorFormField = await this.findFirstInvalidFormField();
      if (errorFormField === undefined) return;
      if (preventScrolling === false) await WindowScrollService.scrollToElement(errorFormField);
      find(errorFormField, ".sde-e-form-field__input")?.focus();
      return;
    }

    await this.postData();
  }

  private async findFirstInvalidFormField() {
    const validStates = await Promise.all(this.formFields.map(formField => formField.isValid()));
    const firstInvalidIndex = validStates.findIndex(isValid => isValid === false);
    return this.formFields[firstInvalidIndex];
  }

  public static async validateFormFields(formFields: Array<FormField<FormFieldType>>): Promise<boolean> {
    const validityStates = await Promise.all(
      formFields.map(async formField => {
        await formField.validate();
        return await formField.isValid();
      }),
    );
    return validityStates.filter(isValid => isValid === false).length === 0;
  }

  private async postData() {
    const submitBody = this.submitBody;

    if (submitBody === undefined || this.submitUrl === "") return;

    this.isLoading = true;

    try {
      const response = await fetch(this.submitUrl, {
        method: "POST",
        body: JSON.stringify(submitBody),
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        this.handleSuccess(response);
      } else {
        this.handleError(response);
      }
    } catch (e: any) {
      this.handleError(e);
    }
  }

  public async reset() {
    await Promise.all(this.formFields.map(formField => formField.reset()));
  }

  private handleError(responseData?: Response) {
    DebuggerService.error("Form Error: ", responseData);
    this.isLoading = false;
    addClass(this, ERROR);
  }

  private handleSuccess(responseData: Response) {
    DebuggerService.log("Form Success: ", responseData);
    this.isLoading = false;
    addClass(this, SUCCESS);
  }
}

customElements.define("sde-m-form", SDEForm);
